import { inject, Service } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Subject } from 'rxjs';

import { CanActivateFn, CanDeactivateFn } from '@angular/router';
import { AuthenticationService } from '@core/authentication';
import { environment } from '@environment';

export const joinAskemoHub: CanActivateFn = async (route, _) => {
    const askemoHubService = inject(AskemoHubService);

    const askemoId = route.paramMap.get('askemoId');

    try {
        await askemoHubService.startConnection();
        await askemoHubService.joinAskemo(askemoId);
    } catch (error) {
        console.error('Failed to join Askemo hub:', error);
        // Allow navigation to continue even if hub connection fails
    }

    return true;
};

export const leaveAskemoHub: CanDeactivateFn<any> = async (_, __, ___, ____) => {
    const askemoHubService = inject(AskemoHubService);

    try {
        await askemoHubService.stopConnection();
    } catch (error) {
        console.error('Failed to leave Askemo hub:', error);
    }

    return true;
};

export interface AskemoSessionStartedEvent {
    sessionId: string;
}

@Service()
export class AskemoHubService {
    private readonly authenticationService = inject(AuthenticationService);
    public readonly contactValidated$ = new Subject<void>();
    public readonly sessionStarted$ = new Subject<AskemoSessionStartedEvent>();
    private readonly hubConnection: HubConnection;
    private connectionPromise: Promise<void> | null = null;

    constructor() {
        this.hubConnection = new HubConnectionBuilder()
            .withAutomaticReconnect()
            .withUrl(`${environment.apiConfig.api.baseUri}hubs/askemo`, {
                accessTokenFactory: () => this.authenticationService.acquireAccessTokenForSignalRHub(),
                transport: HttpTransportType.ServerSentEvents,
            })
            .configureLogging(LogLevel.Warning)
            .build();

        this.hubConnection.on('ContactValidated', () => {
            this.contactValidated$.next();
        });

        this.hubConnection.on('SessionStarted', (sessionId: string) => {
            this.sessionStarted$.next({ sessionId });
        });
    }

    public async startConnection(): Promise<void> {
        if (this.hubConnection.state === HubConnectionState.Connected) {
            return;
        }

        if (this.hubConnection.state === HubConnectionState.Connecting) {
            // Wait for the existing connection attempt
            if (this.connectionPromise) {
                return this.connectionPromise;
            }
        }

        if (this.hubConnection.state === HubConnectionState.Disconnected) {
            this.connectionPromise = this.hubConnection.start();
            try {
                await this.connectionPromise;
            } finally {
                this.connectionPromise = null;
            }
        }
    }

    public async stopConnection(): Promise<void> {
        if (this.hubConnection.state === HubConnectionState.Connected) {
            await this.hubConnection.stop();
        }
        this.connectionPromise = null;
    }

    public async joinAskemo(askemoId: string): Promise<void> {
        await this.ensureConnected();
        return await this.hubConnection.invoke<void>('JoinAskemo', askemoId);
    }

    public async leaveAskemo(askemoId: string): Promise<void> {
        await this.ensureConnected();
        return await this.hubConnection.invoke<void>('LeaveAskemo', askemoId);
    }

    private async ensureConnected(): Promise<void> {
        if (this.hubConnection.state !== HubConnectionState.Connected) {
            await this.startConnection();
        }
    }
}
